import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Modal as RNModal,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ScrollView,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';

interface ModalProps {
  visible: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
  footer?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  visible,
  onClose,
  title,
  children,
  showCloseButton = true,
  footer,
}) => {
  const { colors } = useTheme();
  const [windowHeight, setWindowHeight] = useState(
    Dimensions.get('window').height
  );

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setWindowHeight(window.height);
    });

    return () => subscription?.remove();
  }, []);

  return (
    <RNModal
      visible={visible}
      animationType={Platform.OS === 'web' ? 'fade' : 'slide'}
      presentationStyle={Platform.OS === 'web' ? undefined : 'pageSheet'}
      transparent={Platform.OS === 'web'}
      onRequestClose={onClose}
    >
      <View
        style={[
          Platform.OS === 'web' ? styles.overlay : styles.container,
          {
            backgroundColor:
              Platform.OS === 'web' ? 'rgba(0, 0, 0, 0.5)' : colors.background,
          },
        ]}
      >
        <View
          style={[
            styles.modalContent,
            {
              backgroundColor: colors.card,
              ...(Platform.OS === 'web' && {
                maxHeight: windowHeight * 0.8,
              }),
            },
          ]}
        >
          <View style={[styles.header, { borderBottomColor: colors.border }]}>
            <Text style={[styles.title, { color: colors.text }]}>{title}</Text>
            {showCloseButton && (
              <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                <Ionicons name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            )}
          </View>
          <ScrollView
            style={styles.bodyScrollView}
            contentContainerStyle={styles.bodyContent}
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
          >
            {children}
          </ScrollView>
          {footer && (
            <View style={[styles.footer, { borderTopColor: colors.border }]}>
              {footer}
            </View>
          )}
        </View>
      </View>
    </RNModal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  container: {
    flex: 1,
  },
  modalContent: {
    borderRadius: 12,
    maxWidth: Platform.OS === 'web' ? 500 : '100%',
    width: Platform.OS === 'web' ? '100%' : '100%',
    ...(Platform.OS === 'web' ? {} : { flex: 1 }),
    overflow: 'hidden',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    flexShrink: 0,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    flex: 1,
  },
  closeButton: {
    padding: 4,
  },
  bodyScrollView: {
    flex: 1,
    minHeight: 0,
    flexShrink: 1,
  },
  bodyContent: {
    padding: 16,
  },
  footer: {
    padding: 16,
    borderTopWidth: 1,
    flexShrink: 0,
  },
});

export default Modal;
